import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { formatSandboxPrice } from '../../utils/sandboxCourseApi';

type Props = {
    grossCents: number;
    availableCents: number;
    pendingCents?: number;
    courseCents?: number;
    officeHoursCents?: number;
    holdDays?: number;
    onWithdraw: () => void;
    onPayoutSettings: () => void;
};

export function SandboxStudioEarningsCard({
    grossCents,
    availableCents,
    pendingCents = 0,
    courseCents,
    officeHoursCents,
    holdDays = 7,
    onWithdraw,
    onPayoutSettings,
}: Props) {
    const showBreakdown = (Number(courseCents) || 0) > 0 || (Number(officeHoursCents) || 0) > 0;

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 3,
                bgcolor: OBSIDIAN.surfaceContainerLow,
                p: { xs: 2.5, md: 3 },
                mb: 2.5,
                boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    top: -96,
                    right: -96,
                    width: 224,
                    height: 224,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,201,101,0.15)',
                    filter: 'blur(48px)',
                    pointerEvents: 'none',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: -80,
                    left: -80,
                    width: 192,
                    height: 192,
                    borderRadius: '50%',
                    bgcolor: 'rgba(254,183,0,0.1)',
                    filter: 'blur(40px)',
                    pointerEvents: 'none',
                }}
            />
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography
                            sx={{ fontSize: 12, color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                            Lifetime Earnings
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 800,
                                fontSize: { xs: 30, md: 34 },
                                color: OBSIDIAN.primary,
                                letterSpacing: '-0.03em',
                                mt: 0.5,
                            }}>
                            {formatSandboxPrice(grossCents)}
                        </Typography>
                        {showBreakdown ? (
                            <Typography
                                sx={{
                                    mt: 0.5,
                                    fontSize: 11,
                                    color: OBSIDIAN.onSurfaceVariant,
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                }}>
                                Courses {formatSandboxPrice(courseCents || 0)}
                                {' · '}
                                Office hours {formatSandboxPrice(officeHoursCents || 0)}
                            </Typography>
                        ) : null}
                    </Box>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            bgcolor: OBSIDIAN.surfaceContainer,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: OBSIDIAN.primary,
                        }}>
                        <MonetizationOnIcon sx={{ fontSize: 20 }} />
                    </Box>
                </Box>

                {pendingCents > 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: OBSIDIAN.surfaceContainer,
                            border: `1px solid ${OBSIDIAN.outline}33`,
                        }}>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    color: OBSIDIAN.onSurfaceVariant,
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                }}>
                                Pending (office hours)
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                    fontWeight: 700,
                                    fontSize: 16,
                                    color: OBSIDIAN.onSurface,
                                    mt: 0.25,
                                }}>
                                {formatSandboxPrice(pendingCents)}
                            </Typography>
                        </Box>
                        <Typography
                            sx={{
                                fontSize: 11,
                                color: OBSIDIAN.onSurfaceVariant,
                                fontFamily: OBSIDIAN_BODY_FONT,
                                textAlign: 'right',
                                maxWidth: 180,
                            }}>
                            Visible here, releases to withdraw {holdDays} days after the session
                        </Typography>
                    </Box>
                ) : null}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.75,
                        borderRadius: 2,
                        bgcolor: '#000',
                        flexWrap: 'wrap',
                    }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                color: OBSIDIAN.onSurfaceVariant,
                                fontFamily: OBSIDIAN_BODY_FONT,
                            }}>
                            Available to Withdraw
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 700,
                                fontSize: 20,
                                color: OBSIDIAN.onSurface,
                                mt: 0.25,
                            }}>
                            {formatSandboxPrice(availableCents)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <Button
                            startIcon={<AccountBalanceWalletIcon />}
                            onClick={onWithdraw}
                            sx={{
                                px: 2,
                                py: 1,
                                borderRadius: 999,
                                background: `linear-gradient(90deg, ${OBSIDIAN.primary}, #feb700)`,
                                color: OBSIDIAN.onPrimaryFixed,
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 700,
                                fontSize: 13,
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': { filter: 'brightness(1.05)' },
                            }}>
                            Withdraw
                        </Button>
                    </Box>
                    <Box
                        sx={{
                            display: { xs: 'inline-flex', sm: 'none' },
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: 'rgba(0,108,80,0.3)',
                            color: OBSIDIAN.secondary,
                        }}>
                        <VerifiedIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: 11, fontWeight: 600, fontFamily: OBSIDIAN_BODY_FONT }}>
                            {availableCents > 0 ? 'Cleared' : 'Ready'}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1 }}>
                    <Button
                        fullWidth
                        startIcon={<AccountBalanceWalletIcon />}
                        onClick={onWithdraw}
                        sx={{
                            py: 1.5,
                            borderRadius: 999,
                            background: `linear-gradient(90deg, ${OBSIDIAN.primary}, #feb700, ${OBSIDIAN.primaryDim})`,
                            color: OBSIDIAN.onPrimaryFixed,
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 700,
                            fontSize: 14,
                            textTransform: 'none',
                            boxShadow: '0 8px 24px rgba(255,201,101,0.2)',
                            '&:hover': { filter: 'brightness(1.04)' },
                        }}>
                        Withdraw Funds
                    </Button>
                </Box>

                <Button
                    fullWidth
                    onClick={onPayoutSettings}
                    endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
                    sx={{
                        color: OBSIDIAN.onSurfaceVariant,
                        textTransform: 'none',
                        fontFamily: OBSIDIAN_BODY_FONT,
                        fontSize: 12,
                        fontWeight: 500,
                        py: 0.75,
                        '&:hover': { color: OBSIDIAN.primary, bgcolor: 'transparent' },
                    }}
                    startIcon={<CheckCircleIcon sx={{ fontSize: 16, color: OBSIDIAN.secondary }} />}>
                    Payout Settings
                </Button>

                <Typography
                    sx={{
                        fontSize: 11,
                        color: OBSIDIAN.onSurfaceVariant,
                        fontFamily: OBSIDIAN_BODY_FONT,
                        lineHeight: 1.5,
                    }}>
                    Includes courses and office hours. Pending office-hours pay stays visible but is not withdrawable
                    until after the session, then a {holdDays}-day hold. Course platform fee: 15%.
                </Typography>
            </Box>
        </Box>
    );
}
